import React from 'react';
import { Animated, Platform, View, ViewStyle } from 'react-native';
import { createCellContext } from '@sales-portal/domain/pricing/blocs/utils/create-cell-context';
import { createStyles } from '@/contexts/theme-context';
import { flexRender, Header } from '@tanstack/react-table';
import {
  getCellAlignmentStyle,
  usePricingCellAlignmentStyles,
} from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-cell-alignment-styles';
import { getLeftRightValues } from '@experiences/components/table/blocs/table';
import { PricingEditsBadge } from '@sales-portal/domain/pricing/components/pricing-edits-badge';
import { useTableContext } from '@experiences/components/table/blocs/table-context';

const CHECKBOX_COLUMN_ID = 'checkbox';
const NAME_COLUMN_ID = 'name';

interface PricingAggregateCellProps<TData> {
  data: TData;
  editsCount: number;
  header: Header<TData, unknown>;
  isDeltaColumn: (columnId: string) => boolean;
  isLeftAlignedColumn: (columnId: string) => boolean;
}

const PricingAggregateCellImpl = <TData,>({
  data,
  editsCount,
  header,
  isDeltaColumn,
  isLeftAlignedColumn,
}: PricingAggregateCellProps<TData>) => {
  const [styles] = useStyles();
  const [alignmentStyles] = usePricingCellAlignmentStyles();
  const { cellPadding, columnWidths, pinnedScrollOffset } = useTableContext();

  const columnId = header.column.id;
  const width = columnWidths[columnId];
  const isPinned = header.column.getIsPinned();
  const pinnedOffset = isPinned ? header.column.getStart('left') : undefined;
  const shouldAnimatePin =
    isPinned && pinnedScrollOffset && Platform.OS !== 'web';

  const pinnedStyles: ViewStyle[] = isPinned
    ? [
        styles.pinned,
        Platform.OS === 'web' ? { left: pinnedOffset } : { left: -1 },
      ]
    : [];

  if (columnId === CHECKBOX_COLUMN_ID) {
    const checkboxCellStyle: ViewStyle[] = [
      styles.cell,
      styles.highlighted,
      { width },
      ...pinnedStyles,
    ];

    if (shouldAnimatePin) {
      return (
        <Animated.View
          style={[
            checkboxCellStyle,
            { transform: [{ translateX: pinnedScrollOffset }] },
          ]}
        />
      );
    }

    return <View style={checkboxCellStyle} />;
  }

  const columnIndex = header.column.getIndex();
  const columnPadding = Array.isArray(cellPadding)
    ? (cellPadding[columnIndex] ?? [0, 0])
    : cellPadding;
  const [paddingLeft, paddingRight] = getLeftRightValues(columnPadding);

  const isNameColumn = columnId === NAME_COLUMN_ID;
  const alignmentStyle = getCellAlignmentStyle({
    isDelta: isDeltaColumn(columnId),
    isLeftAligned: isLeftAlignedColumn(columnId),
    styles: alignmentStyles,
  });

  const cellStyles: ViewStyle[] = [
    styles.cell,
    styles.highlighted,
    { paddingLeft, paddingRight, width },
    alignmentStyle ?? {},
    ...pinnedStyles,
    isNameColumn ? styles.nameCell : {},
  ];

  const context = createCellContext(header, data, columnIndex);

  const cellContent = flexRender(header.column.columnDef.cell, context);
  const content = isNameColumn ? (
    <>
      <View style={styles.nameContent}>{cellContent}</View>
      {editsCount ? <PricingEditsBadge editsCount={editsCount} /> : null}
    </>
  ) : (
    cellContent
  );

  if (shouldAnimatePin) {
    return (
      <Animated.View
        style={[
          cellStyles,
          { transform: [{ translateX: pinnedScrollOffset }] },
        ]}
      >
        {content}
      </Animated.View>
    );
  }

  return <View style={cellStyles}>{content}</View>;
};

export const PricingAggregateCell = React.memo(
  PricingAggregateCellImpl,
) as typeof PricingAggregateCellImpl;

const useStyles = createStyles((theme) => ({
  cell: {
    backgroundColor: theme.sk.color.bg.primary.default,
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  highlighted: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  nameCell: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
    justifyContent: 'space-between',
    paddingRight: theme.sk.spacing.spaceMedium,
  },
  nameContent: {
    flexShrink: 1,
  },
  pinned: {
    ...Platform.select({
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    zIndex: 1,
  },
}));
